/* ============================================================
   Purest Solutions — Melbourne Tap Water Checker
   Namespaced, framework-free, progressive-enhancement JS.
   Fetches assets/water-checker-data.json, drives search/
   autocomplete, URL state, and renders the per-suburb profile,
   quality snapshot, history timeline, property questionnaire
   and personalised summary from that single data source.
   ============================================================ */
(function () {
  'use strict';

  var root = document.getElementById('PSWaterChecker');
  if (!root) return;

  var configEl = document.getElementById('ps-wc-config');
  var config = {};
  try { config = configEl ? JSON.parse(configEl.textContent) : {}; } catch (e) { config = {}; }
  if (!config.dataUrl) return;

  /* ── DOM refs ─────────────────────────────────────────── */
  var els = {
    form: document.getElementById('ps-wc-search-form'),
    input: document.getElementById('ps-wc-suburb-input'),
    suggestions: document.getElementById('ps-wc-suggestions'),
    loading: document.getElementById('ps-wc-loading'),
    exampleRow: document.getElementById('ps-wc-example-chips'),
    results: document.getElementById('ps-wc-results'),
    stateUnknown: document.getElementById('ps-wc-state-unknown'),
    stateOutside: document.getElementById('ps-wc-state-outside'),
    stateMultiple: document.getElementById('ps-wc-state-multiple'),
    multipleOptions: document.getElementById('ps-wc-multiple-options'),
    suburbSuggestions: document.getElementById('ps-wc-suburb-suggestions'),
    profileSection: document.getElementById('ps-wc-profile-section'),
    profileTitle: document.getElementById('ps-wc-profile-title'),
    profileCtaTitle: document.getElementById('ps-wc-profile-cta-title'),
    profileCtaBody: document.getElementById('ps-wc-profile-cta-body'),
    scoreboard: document.getElementById('ps-wc-scoreboard'),
    summaryGrid: document.getElementById('ps-wc-summary-grid'),
    freshnessPanel: document.getElementById('ps-wc-freshness-panel'),
    decisionSection: document.getElementById('ps-wc-decision-section'),
    filterMatchCard: document.getElementById('ps-wc-filter-match-card'),
    providerFirstCard: document.getElementById('ps-wc-provider-first-card'),
    qualitySection: document.getElementById('ps-wc-quality-section'),
    qualityGrid: document.getElementById('ps-wc-quality-grid'),
    historySection: document.getElementById('ps-wc-history-section'),
    historyTimeline: document.getElementById('ps-wc-history-timeline'),
    propertySection: document.getElementById('ps-wc-property-section'),
    propertyForm: document.getElementById('ps-wc-property-form'),
    propertySubmit: document.getElementById('ps-wc-property-submit'),
    propertySkip: document.getElementById('ps-wc-property-skip'),
    summarySection: document.getElementById('ps-wc-summary-section'),
    summaryBlocks: document.getElementById('ps-wc-summary-blocks'),
    ctaSection: document.getElementById('ps-wc-cta-section'),
    ctaChips: document.getElementById('ps-wc-cta-chips'),
    ctaFacts: document.getElementById('ps-wc-cta-facts'),
    tplQualityCard: document.getElementById('ps-wc-tpl-quality-card'),
    tplHistoryEvent: document.getElementById('ps-wc-tpl-history-event')
  };

  var DATA = null;
  var suburbIndex = [];
  var activeSuburb = null;
  var activeOptionIndex = -1;
  var currentMatches = [];

  /* ── Analytics (pushes to window.dataLayer if present; no-op otherwise) ── */
  function track(eventName, detail) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (detail) {
      for (var k in detail) { if (Object.prototype.hasOwnProperty.call(detail, k)) payload[k] = detail[k]; }
    }
    window.dataLayer.push(payload);
  }

  /* ── Utilities ────────────────────────────────────────── */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function slugify(str) {
    return String(str || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  function normalize(str) {
    return String(str || '').toLowerCase().trim();
  }

  function byId(list, id) {
    for (var i = 0; i < list.length; i++) { if (list[i].id === id) return list[i]; }
    return null;
  }

  function providerFor(suburb) { return byId(DATA.providers, suburb.provider_id); }
  function zoneFor(suburb) { return byId(DATA.zones, suburb.zone_id); }

  function suburbAnalytics(record, provider, zone) {
    return {
      suburb: record ? record.suburb : null,
      postcode: record ? record.postcode : null,
      provider_id: provider ? provider.id : null,
      provider_name: provider ? provider.name : null,
      zone_id: zone ? zone.id : null,
      zone_name: zone ? zone.name : null,
      mapping_confidence: record ? record.mapping_confidence : null
    };
  }

  function isPending(val) {
    return val === undefined || val === null || val === '' || val === 'DATA_PENDING';
  }

  function numericValue(val) {
    if (isPending(val)) return null;
    var match = String(val).replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }

  function setHidden(el, hidden) { if (el) el.hidden = !!hidden; }

  function clearNode(el) { while (el && el.firstChild) el.removeChild(el.firstChild); }

  function qualityResultsForZone(zone) {
    return zone ? (DATA.water_quality_results || []).filter(function (r) {
      return r.zone_id === zone.id && !isPending(r.value);
    }) : [];
  }

  function providerZoneForResult(result) {
    return result && result.zone_id ? byId(DATA.zones || [], result.zone_id) : null;
  }

  function cloneResult(result) {
    var copy = {};
    for (var key in result) {
      if (Object.prototype.hasOwnProperty.call(result, key)) copy[key] = result[key];
    }
    return copy;
  }

  function fallbackResultForProvider(provider, parameterId) {
    if (!provider) return null;
    var matches = (DATA.water_quality_results || []).filter(function (result) {
      var resultZone = providerZoneForResult(result);
      return result.parameter_id === parameterId &&
        !isPending(result.value) &&
        result.scope === 'system_wide' &&
        resultZone &&
        resultZone.provider_id === provider.id;
    });
    return matches[0] || null;
  }

  function displayResultsForZone(zone, provider) {
    var exactResults = qualityResultsForZone(zone);
    return (DATA.parameters || []).map(function (param) {
      var exact = resultFor(exactResults, param.id);
      if (exact) return exact;

      var fallback = fallbackResultForProvider(provider, param.id);
      if (fallback) {
        var copy = cloneResult(fallback);
        copy._fallback = !zone || fallback.zone_id !== zone.id;
        return copy;
      }

      return {
        parameter_id: param.id,
        value: 'Not published',
        unit: '',
        scope: 'not_published',
        unavailable: true,
        source_org: provider ? provider.name : null
      };
    });
  }

  /* ── Load data ────────────────────────────────────────── */
  function loadData() {
    setHidden(els.loading, false);
    if (els.loading) els.loading.classList.add('is-visible');
    return fetch(config.dataUrl, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Failed to load water-checker data (' + r.status + ')');
        return r.json();
      })
      .then(function (json) {
        DATA = json;
        buildSuburbIndex();
        handleInitialUrlState();
      })
      .catch(function () {
        // Fail quietly but visibly: disable search rather than break the page.
        if (els.input) {
          els.input.disabled = true;
          els.input.placeholder = 'Water data is temporarily unavailable';
        }
      })
      .finally(function () {
        if (els.loading) els.loading.classList.remove('is-visible');
      });
  }

  function buildSuburbIndex() {
    suburbIndex = (DATA.suburbs || []).map(function (s) {
      return {
        record: s,
        label: s.suburb + ' VIC ' + s.postcode,
        searchText: normalize(s.suburb + ' ' + s.postcode + ' vic')
      };
    });
  }

  /* ── Autocomplete ─────────────────────────────────────── */
  function matchSuburbs(query) {
    var q = normalize(query);
    if (!q) return [];
    var starts = [], contains = [];
    suburbIndex.forEach(function (entry) {
      var nameNorm = normalize(entry.record.suburb);
      if (nameNorm.indexOf(q) === 0 || entry.record.postcode.indexOf(q) === 0) {
        starts.push(entry);
      } else if (entry.searchText.indexOf(q) !== -1) {
        contains.push(entry);
      }
    });
    return starts.concat(contains).slice(0, 8);
  }

  function renderSuggestions(matches) {
    currentMatches = matches;
    activeOptionIndex = -1;
    clearNode(els.suggestions);

    if (!matches.length) {
      els.suggestions.classList.remove('is-open');
      els.input.setAttribute('aria-expanded', 'false');
      return;
    }

    matches.forEach(function (entry, i) {
      var li = document.createElement('li');
      li.className = 'ps-wc-suggestion';
      li.id = 'ps-wc-opt-' + i;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');

      var strong = document.createElement('strong');
      strong.textContent = entry.record.suburb;
      var span = document.createElement('span');
      span.textContent = 'VIC ' + entry.record.postcode;

      li.appendChild(strong);
      li.appendChild(span);
      li.addEventListener('mousedown', function (e) {
        e.preventDefault();
        selectSuburb(entry.record);
      });
      els.suggestions.appendChild(li);
    });

    els.suggestions.classList.add('is-open');
    els.input.setAttribute('aria-expanded', 'true');
  }

  function moveActiveOption(delta) {
    var options = els.suggestions.querySelectorAll('.ps-wc-suggestion');
    if (!options.length) return;
    activeOptionIndex = (activeOptionIndex + delta + options.length) % options.length;
    options.forEach(function (opt, i) {
      var active = i === activeOptionIndex;
      opt.classList.toggle('is-active', active);
      opt.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    els.input.setAttribute('aria-activedescendant', options[activeOptionIndex].id);
  }

  function closeSuggestions() {
    els.suggestions.classList.remove('is-open');
    els.input.setAttribute('aria-expanded', 'false');
    els.input.removeAttribute('aria-activedescendant');
    activeOptionIndex = -1;
  }

  var onInput = debounce(function () {
    var val = els.input.value;
    if (val.length === 0) { closeSuggestions(); return; }
    track('suburb_search_started', { query_length: val.length });
    renderSuggestions(matchSuburbs(val));
  }, 120);

  if (els.input) {
    els.input.addEventListener('input', onInput);

    els.input.addEventListener('keydown', function (e) {
      var isOpen = els.suggestions.classList.contains('is-open');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) { renderSuggestions(matchSuburbs(els.input.value)); }
        else { moveActiveOption(1); }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (isOpen) moveActiveOption(-1);
      } else if (e.key === 'Escape') {
        closeSuggestions();
      } else if (e.key === 'Enter') {
        if (isOpen && activeOptionIndex >= 0 && currentMatches[activeOptionIndex]) {
          e.preventDefault();
          selectSuburb(currentMatches[activeOptionIndex].record);
        }
      }
    });

    els.input.addEventListener('blur', function () {
      setTimeout(closeSuggestions, 100);
    });
  }

  if (els.exampleRow) {
    els.exampleRow.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-example]');
      if (!btn) return;
      els.input.value = btn.dataset.example;
      resolveQuery(btn.dataset.example);
    });
  }

  if (els.form) {
    els.form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (activeOptionIndex >= 0 && currentMatches[activeOptionIndex]) {
        selectSuburb(currentMatches[activeOptionIndex].record);
        return;
      }
      resolveQuery(els.input.value);
    });
  }

  /* ── Resolve a free-text query to a suburb / error state ── */
  function resolveQuery(query) {
    closeSuggestions();
    var q = normalize(query);
    if (!q) return;

    var postcodeOnly = /^\d{4}$/.test(q);

    if (postcodeOnly) {
      var vicRange = (q >= '3000' && q <= '3999') || (q >= '8000' && q <= '8999');
      var byPostcode = suburbIndex.filter(function (e) { return e.record.postcode === q; });
      if (byPostcode.length === 1) { selectSuburb(byPostcode[0].record); return; }
      if (byPostcode.length > 1) { showMultiple(byPostcode.map(function (e) { return e.record; })); return; }
      if (!vicRange) { showOutside(); return; }
      showUnknown();
      return;
    }

    // Exact suburb name match (ignoring an optional trailing state/postcode)
    var cleaned = q.replace(/\bvic\b/g, '').replace(/\d{4}\b/g, '').trim();
    var exact = suburbIndex.filter(function (e) { return normalize(e.record.suburb) === cleaned; });
    if (exact.length === 1) { selectSuburb(exact[0].record); return; }
    if (exact.length > 1) { showMultiple(exact.map(function (e) { return e.record; })); return; }

    var fuzzy = matchSuburbs(query);
    if (fuzzy.length === 1) { selectSuburb(fuzzy[0].record); return; }
    if (fuzzy.length > 1) { showMultiple(fuzzy.map(function (e) { return e.record; })); return; }

    showUnknown();
  }

  /* ── Error / empty states ─────────────────────────────── */
  function hideAllStates() {
    setHidden(els.stateUnknown, true);
    setHidden(els.stateOutside, true);
    setHidden(els.stateMultiple, true);
    setHidden(els.profileSection, true);
    setHidden(els.decisionSection, true);
    setHidden(els.qualitySection, true);
    setHidden(els.historySection, true);
    setHidden(els.propertySection, true);
    setHidden(els.summarySection, true);
    setHidden(els.ctaSection, true);
  }

  function showResultsRoot() {
    els.results.classList.add('is-visible');
  }

  function suburbLinkButton(record) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ps-wc-btn ps-wc-btn--secondary';
    btn.textContent = record.suburb + ' ' + record.postcode;
    btn.addEventListener('click', function () { selectSuburb(record); });
    return btn;
  }

  function showUnknown() {
    hideAllStates();
    showResultsRoot();
    track('water_checker_no_match', {
      query_length: els.input && els.input.value ? els.input.value.length : 0
    });
    setHidden(els.stateUnknown, false);
    clearNode(els.suburbSuggestions);
    suburbIndex.slice(0, 6).forEach(function (e) {
      els.suburbSuggestions.appendChild(suburbLinkButton(e.record));
    });
    els.stateUnknown.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function showOutside() {
    hideAllStates();
    showResultsRoot();
    track('water_checker_outside_region', {
      query_length: els.input && els.input.value ? els.input.value.length : 0
    });
    setHidden(els.stateOutside, false);
    els.stateOutside.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function showMultiple(records) {
    hideAllStates();
    showResultsRoot();
    track('water_checker_multiple_matches', { match_count: records.length });
    setHidden(els.stateMultiple, false);
    clearNode(els.multipleOptions);
    records.forEach(function (r) { els.multipleOptions.appendChild(suburbLinkButton(r)); });
    els.stateMultiple.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ── URL state ────────────────────────────────────────── */
  function updateUrl(record) {
    if (!window.history || !window.history.pushState) return;
    var url = new URL(window.location.href);
    var area = slugify(record.suburb);
    url.hash = area;
    url.searchParams.delete('area');
    url.searchParams.delete('suburb');
    url.searchParams.delete('postcode');
    window.history.pushState({ area: area, suburb: record.suburb }, '', url.toString());
  }

  function handleInitialUrlState() {
    var params = new URLSearchParams(window.location.search);
    var hashParam = window.location.hash ? window.location.hash.replace(/^#/, '') : null;
    var areaParam = hashParam || params.get('area');
    var suburbParam = params.get('suburb');
    var postcodeParam = params.get('postcode');
    if (!areaParam && !suburbParam && !postcodeParam) return;

    var match = suburbIndex.filter(function (e) {
      var slugMatch = areaParam
        ? slugify(e.record.suburb) === slugify(areaParam)
        : (suburbParam ? slugify(e.record.suburb) === slugify(suburbParam) : true);
      var pcMatch = postcodeParam ? e.record.postcode === postcodeParam : true;
      return slugMatch && pcMatch;
    });

    if (match.length === 1) {
      renderProfile(match[0].record);
      showResultsRoot();
      if (areaParam || suburbParam || postcodeParam) {
        updateUrl(match[0].record);
      }
    }
  }

  /* ── Select + render profile ──────────────────────────── */
  function selectSuburb(record) {
    closeSuggestions();
    if (els.input) els.input.value = record.suburb + ' ' + record.postcode;
    track('suburb_selected', { suburb: record.suburb, postcode: record.postcode });
    updateUrl(record);
    renderProfile(record);
    showResultsRoot();
    if (els.profileSection) els.profileSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderProfile(record) {
    activeSuburb = record;
    hideAllStates();

    var provider = providerFor(record);
    var zone = zoneFor(record);
    var qualityResults = zone ? qualityResultsForZone(zone) : [];
    var displayResults = displayResultsForZone(zone, provider);
    var analytics = suburbAnalytics(record, provider, zone);
    analytics.quality_result_count = qualityResults.length;
    analytics.display_result_count = displayResults.filter(function (r) { return r && !r.unavailable; }).length;
    track('water_checker_result_viewed', analytics);

    els.profileTitle.textContent = record.suburb + ', VIC ' + record.postcode;
    if (els.profileCtaTitle) els.profileCtaTitle.textContent = 'Questions about ' + record.suburb + ' water?';
    if (els.profileCtaBody) {
      els.profileCtaBody.textContent = 'This profile uses ' + qualityResults.length + ' sourced local reading' + (qualityResults.length === 1 ? '' : 's') + ' where available. Ask us about taste, odour, sediment or the best filtration setup for your home.';
    }

    renderScoreboard(record, provider, zone, displayResults);
    clearNode(els.summaryGrid);
    setHidden(els.summaryGrid, true);
    clearNode(els.freshnessPanel);
    setHidden(els.freshnessPanel, true);
    renderDecisionCards(record, provider, zone, qualityResults);

    setHidden(els.profileSection, false);
    setHidden(els.decisionSection, false);
    setHidden(els.qualitySection, false);
    renderQuality(zone, provider);
    renderHistory(record, provider, zone);
    renderCtaFacts(record, provider, zone);
    renderPropertyForm();
    setHidden(els.propertySection, false);
    setHidden(els.ctaSection, false);

    track('profile_loaded', { suburb: record.suburb, postcode: record.postcode, provider: provider ? provider.name : null });
  }

  function addSummaryItem(label, value, muted, icon) {
    var item = document.createElement('div');
    item.className = 'ps-wc-summary-item';
    if (icon && PARAM_ICONS[icon]) {
      var iconWrap = document.createElement('span');
      iconWrap.className = 'ps-wc-summary-icon';
      iconWrap.innerHTML = PARAM_ICONS[icon];
      item.appendChild(iconWrap);
    }
    var textWrap = document.createElement('div');
    var l = document.createElement('p');
    l.className = 'ps-wc-summary-label';
    l.textContent = label;
    var v = document.createElement('p');
    v.className = 'ps-wc-summary-value' + (muted ? ' ps-wc-summary-value--muted' : '');
    v.textContent = value;
    textWrap.appendChild(l);
    textWrap.appendChild(v);
    item.appendChild(textWrap);
    els.summaryGrid.appendChild(item);
  }

  function storyStatusText(parameterId, result) {
    if (!result || result.unavailable) return { value: 'Not published', body: 'No verified reading is currently mapped for this part of the profile.' };
    var value = numericValue(result.value);
    if (value === null) return { value: shortValue(result), body: 'This result is included, but it is not a simple number to grade.' };

    if (parameterId === 'chlorine') {
      if (value <= 0.3) return { value: 'Low taste signal', body: 'Reported chlorine is low. Some people can still notice it, but this is not a strong suburb-level taste flag.' };
      if (value <= 0.5) return { value: 'Mild taste signal', body: 'Reported chlorine is around the range where sensitive households may notice taste or smell.' };
      return { value: 'May be noticeable', body: 'Chlorine is still below health limits, but taste or smell may be a real everyday concern.' };
    }

    if (parameterId === 'turbidity') {
      if (value <= 1) return { value: 'Clear profile', body: 'The reported turbidity is low, which usually points to clear water in the monitored supply.' };
      if (value <= 5) return { value: 'Worth watching', body: 'The reading remains within the guideline shown, but clarity or sediment questions are reasonable.' };
      return { value: 'Check source report', body: 'This is above the guideline shown and should be checked against the original authority report.' };
    }

    if (parameterId === 'hardness') {
      if (value < 60) return { value: 'Soft water', body: 'Scale around kettles, taps and appliances should usually be lower than in hard-water regions.' };
      if (value < 200) return { value: 'Moderate hardness', body: 'Scale can become more noticeable as hardness rises, even where the water remains usable.' };
      return { value: 'Scale likely', body: 'Hardness is high enough that scale management may be a practical concern.' };
    }

    return { value: shortValue(result), body: parameterInsight(byId(DATA.parameters || [], parameterId), result) || 'Use this as network context, not a property-specific tap test.' };
  }

  function metalsStory(results) {
    var lead = resultFor(results, 'lead');
    var copper = resultFor(results, 'copper');
    var leadValue = lead && !lead.unavailable ? numericValue(lead.value) : null;
    var copperValue = copper && !copper.unavailable ? numericValue(copper.value) : null;
    if (leadValue === null && copperValue === null) {
      return { value: 'Home-specific', body: 'Metals are highly influenced by internal plumbing, fittings and first-draw tap conditions.' };
    }
    if ((leadValue !== null && leadValue > 0.001) || (copperValue !== null && copperValue > 0.05)) {
      return { value: 'Plumbing worth checking', body: 'Network readings are below the guideline shown, but metals can still vary at a specific tap.' };
    }
    return { value: 'Low network signal', body: 'The mapped network readings do not point to a strong metals issue, but older household plumbing can still matter.' };
  }

  function renderStoryGrid(record, provider, zone, results) {
    var chlorine = storyStatusText('chlorine', resultFor(results, 'chlorine'));
    var clarity = storyStatusText('turbidity', resultFor(results, 'turbidity'));
    var hardness = storyStatusText('hardness', resultFor(results, 'hardness'));
    var metals = metalsStory(results);

    [
      { label: 'Taste and smell', value: chlorine.value, body: chlorine.body, icon: 'droplet' },
      { label: 'Clarity and sediment', value: clarity.value, body: clarity.body, icon: 'wave' },
      { label: 'Scale and appliances', value: hardness.value, body: hardness.body, icon: 'crystal' },
      { label: 'Tap and plumbing context', value: metals.value, body: metals.body, icon: 'pipe' }
    ].forEach(function (item) {
      addSummaryItem(item.label, item.value + '. ' + item.body, false, item.icon);
    });
  }

  function readableDate(value) {
    if (isPending(value)) return 'Not recorded';
    var d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function sourceYearSummary(results) {
    var years = [];
    results.forEach(function (result) {
      if (result.reporting_period && years.indexOf(result.reporting_period) === -1) years.push(result.reporting_period);
    });
    return years.length ? years.join(', ') : 'Latest verified source';
  }

  function mappingCoverageLabel(record) {
    if (record && record.mapping_confidence === 'primary_table') return 'Department of Health mapped supply-zone';
    if (record && record.mapping_confidence === 'split_sampling_area') return 'split sampling-area context';
    if (record && record.mapping_confidence === 'unresolved_primary_table') return 'unresolved Health-table sampling area';
    if (record && record.mapping_confidence === 'secondary_cross_check') return 'indicative cross-checked supply-zone';
    return 'provider supply-zone';
  }

  function isIndicativeMapping(record) {
    return !!(record && record.mapping_confidence !== 'primary_table');
  }

  function incidentsFor(record, provider, zone) {
    return (DATA.incidents || []).filter(function (incident) {
      return incidentMatches(incident, record, provider, zone) && !isPending(incident.summary);
    });
  }

  function filterOpportunitySummary(record, results) {
    var chlorine = resultFor(results, 'chlorine');
    var turbidity = resultFor(results, 'turbidity');
    var hardness = resultFor(results, 'hardness');
    var lead = resultFor(results, 'lead');
    var copper = resultFor(results, 'copper');
    var messages = [];

    if (chlorine && !chlorine.unavailable) messages.push('carbon filtration may reduce chlorine taste and odour at the tap');
    if (turbidity && !turbidity.unavailable) messages.push('sediment filtration may help with visible particles or cloudiness');
    if (hardness && !hardness.unavailable && numericValue(hardness.value) >= 60) messages.push('hardness context can guide scale-management choices');
    if ((lead && !lead.unavailable) || (copper && !copper.unavailable)) messages.push('metal concerns should be matched to certified reduction claims and home plumbing context');

    if (!messages.length) {
      messages.push('filtration may still improve everyday taste, odour and convenience depending on the product and cartridge');
    }

    return {
      title: 'How filtration fits',
      body: 'For ' + record.suburb + ', the most sensible conversation is: ' + messages.slice(0, 2).join('; ') + '.',
      detail: 'Exact results depend on the installed product, cartridge condition, flow rate and your own plumbing.'
    };
  }

  function journeyPrimarySignal(results) {
    var chlorine = resultFor(results, 'chlorine');
    var turbidity = resultFor(results, 'turbidity');
    var hardness = resultFor(results, 'hardness');
    var lead = resultFor(results, 'lead');
    var copper = resultFor(results, 'copper');
    var chlorineValue = chlorine && !chlorine.unavailable ? numericValue(chlorine.value) : null;
    var turbidityValue = turbidity && !turbidity.unavailable ? numericValue(turbidity.value) : null;
    var hardnessValue = hardness && !hardness.unavailable ? numericValue(hardness.value) : null;
    var leadValue = lead && !lead.unavailable ? numericValue(lead.value) : null;
    var copperValue = copper && !copper.unavailable ? numericValue(copper.value) : null;

    if (turbidityValue !== null && turbidityValue > 1) {
      return {
        label: 'Clarity and sediment',
        title: 'Worth checking at the tap',
        body: 'The reported clarity reading is still within the guideline shown, but visible particles, cloudy water or recurring sediment are worth a proper conversation.'
      };
    }

    if (chlorineValue !== null && chlorineValue > 0.3) {
      return {
        label: 'Taste and smell',
        title: 'Chlorine may be noticeable',
        body: 'The reported chlorine level is below the health references shown, but it can still affect everyday taste and smell for sensitive households.'
      };
    }

    if ((leadValue !== null && leadValue > 0.001) || (copperValue !== null && copperValue > 0.05)) {
      return {
        label: 'Tap and plumbing',
        title: 'Your home setup matters',
        body: 'Network readings are useful context, but metals can change at the actual tap depending on pipes, fittings and first-draw conditions.'
      };
    }

    if (hardnessValue !== null && hardnessValue < 60) {
      return {
        label: 'Scale and appliances',
        title: 'Soft water profile',
        body: 'Scale should usually be lower than hard-water regions, so filtration is more likely to be about taste, odour, particles and convenience.'
      };
    }

    return {
      label: 'Everyday tap quality',
      title: 'Use this as your baseline',
      body: 'The useful next step is comparing the supply-zone profile with what you actually notice at home: taste, smell, sediment, scale or plumbing age.'
    };
  }

  function journeyMetric(label, value, body, tone) {
    var node = document.createElement('div');
    node.className = 'ps-wc-journey-metric' + (tone ? ' ps-wc-journey-metric--' + tone : '');
    var small = document.createElement('span');
    small.textContent = label;
    var strong = document.createElement('strong');
    strong.textContent = value;
    var copy = document.createElement('p');
    copy.textContent = body;
    node.appendChild(small);
    node.appendChild(strong);
    node.appendChild(copy);
    return node;
  }

  function journeyResultValue(results, parameterId) {
    var result = resultFor(results, parameterId);
    if (!result || result.unavailable) return 'Not published';
    return shortValue(result);
  }

  function renderScoreboard(record, provider, zone, results) {
    if (!els.scoreboard) return;
    clearNode(els.scoreboard);

    var filter = filterOpportunitySummary(record, results);
    var signal = journeyPrimarySignal(results);
    var exactCount = zone ? qualityResultsForZone(zone).length : 0;
    var providerText = provider ? provider.name : 'Provider not confirmed';
    var supplyText = zone ? zone.name : providerText;
    var eColi = resultFor(results, 'e_coli');
    var safetyText = eColi && !eColi.unavailable ? shortValue(eColi) : 'Check source';
    var leadTitle = record.suburb + ' water, in plain English';
    var leadBody = provider
      ? record.suburb + ' is mapped to ' + providerText + ' supply context. Use this as a suburb-level guide for taste, clarity and filtration choices, not as a live alert or a lab test of your individual tap.'
      : 'Use this as a suburb-level guide for taste, clarity and filtration choices. It is not a live alert or a lab test of your individual tap.';

    var hero = document.createElement('div');
    hero.className = 'ps-wc-journey-hero';

    var copy = document.createElement('div');
    copy.className = 'ps-wc-journey-copy';
    var eyebrow = document.createElement('span');
    eyebrow.className = 'ps-wc-journey-eyebrow';
    eyebrow.textContent = signal.label;
    var title = document.createElement('h3');
    title.textContent = leadTitle;
    var body = document.createElement('p');
    body.textContent = leadBody;
    var signalNode = document.createElement('div');
    signalNode.className = 'ps-wc-journey-signal';
    var signalTitle = document.createElement('strong');
    signalTitle.textContent = signal.title;
    var signalBody = document.createElement('span');
    signalBody.textContent = signal.body;
    signalNode.appendChild(signalTitle);
    signalNode.appendChild(signalBody);

    var actions = document.createElement('div');
    actions.className = 'ps-wc-journey-actions';
    var enquire = document.createElement('a');
    var configuredEnquire = root.querySelector('#ps-wc-profile-cta .ps-wc-btn--primary');
    var configuredShop = root.querySelector('#ps-wc-profile-cta .ps-wc-btn--secondary');
    enquire.href = configuredEnquire ? configuredEnquire.getAttribute('href') : '/pages/contact';
    enquire.className = 'ps-wc-btn ps-wc-btn--primary';
    enquire.textContent = 'Ask Purest about my water';
    var shop = document.createElement('a');
    shop.href = configuredShop ? configuredShop.getAttribute('href') : '/collections/all';
    shop.className = 'ps-wc-btn ps-wc-btn--secondary';
    shop.textContent = 'See filtration options';
    actions.appendChild(enquire);
    actions.appendChild(shop);

    copy.appendChild(eyebrow);
    copy.appendChild(title);
    copy.appendChild(body);
    copy.appendChild(signalNode);
    copy.appendChild(actions);

    var metrics = document.createElement('div');
    metrics.className = 'ps-wc-journey-metrics';
    metrics.appendChild(journeyMetric('Local supply', supplyText, exactCount + ' sourced reading' + (exactCount === 1 ? '' : 's') + ' currently mapped.', 'neutral'));
    metrics.appendChild(journeyMetric('Safety indicator', safetyText, 'E. coli compliance context, not taste.', 'good'));
    metrics.appendChild(journeyMetric('Chlorine', journeyResultValue(results, 'chlorine'), 'Taste and smell context.', 'watch'));
    metrics.appendChild(journeyMetric('Clarity', journeyResultValue(results, 'turbidity'), 'Sediment and cloudiness context.', 'watch'));

    var reference = document.createElement('div');
    reference.className = 'ps-wc-journey-reference';
    var referenceStrong = document.createElement('strong');
    referenceStrong.textContent = 'What Purest filtration can target';
    var referenceSpan = document.createElement('span');
    referenceSpan.textContent = 'Carbon + sediment filtration is designed for chlorine taste and odour plus sediment and visible particles. Broader concerns such as heavy metals, PFAS and microplastics should be matched to the exact cartridge specification. ' + filter.body;
    reference.appendChild(referenceStrong);
    reference.appendChild(referenceSpan);

    hero.appendChild(copy);
    hero.appendChild(metrics);
    els.scoreboard.appendChild(hero);
    els.scoreboard.appendChild(reference);
  }

  function renderFreshnessPanel(record, provider, zone, results) {
    if (!els.freshnessPanel) return;
    clearNode(els.freshnessPanel);
    var details = document.createElement('details');
    details.className = 'ps-wc-freshness-details';
    var summary = document.createElement('summary');
    summary.textContent = 'How this result was sourced';
    details.appendChild(summary);

    var grid = document.createElement('div');
    grid.className = 'ps-wc-freshness-grid';
    [
      { label: 'Latest annual data used', value: sourceYearSummary(results) },
      { label: 'Dataset checked', value: readableDate(DATA.meta && DATA.meta.last_updated) },
      { label: 'Live alerts', value: 'Not a live alert service' },
      { label: 'Coverage type', value: zone ? (results.some(function (r) { return r.scope === 'system_wide'; }) ? 'Mixed zone/network data' : mappingCoverageLabel(record)) : 'Provider not verified' }
    ].forEach(function (item) {
      var node = document.createElement('div');
      node.className = 'ps-wc-freshness-item';
      var label = document.createElement('span');
      label.textContent = item.label;
      var value = document.createElement('strong');
      value.textContent = item.value;
      node.appendChild(label);
      node.appendChild(value);
      grid.appendChild(node);
    });
    details.appendChild(grid);
    els.freshnessPanel.appendChild(details);
  }

  function filterRecommendation(results, incidentCount) {
    var chlorine = resultFor(results, 'chlorine');
    var hardness = resultFor(results, 'hardness');
    var turbidity = resultFor(results, 'turbidity');
    var lead = resultFor(results, 'lead');
    var copper = resultFor(results, 'copper');
    var chlorineValue = chlorine ? numericValue(chlorine.value) : null;
    var hardnessValue = hardness ? numericValue(hardness.value) : null;
    var turbidityValue = turbidity ? numericValue(turbidity.value) : null;
    var leadValue = lead ? numericValue(lead.value) : null;
    var copperValue = copper ? numericValue(copper.value) : null;
    var reasons = [];
    var level = 'Low';

    if (chlorineValue !== null && chlorineValue >= 0.5) {
      level = 'Medium';
      reasons.push('chlorine taste or smell may be noticeable even below health limits');
    }
    if (turbidityValue !== null && turbidityValue > 1) {
      level = 'Medium';
      reasons.push('clarity or sediment context is worth checking');
    }
    if (hardnessValue !== null && hardnessValue >= 60) {
      level = 'Medium';
      reasons.push('hardness may contribute to scale');
    }
    if ((leadValue !== null && leadValue > 0.001) || (copperValue !== null && copperValue > 0.05)) {
      level = 'Medium';
      reasons.push('tap-specific plumbing may matter for metals');
    }
    if (incidentCount > 0) reasons.push('historical water-quality context is recorded for this area');
    if (!reasons.length) reasons.push('reported authority readings do not point to a strong suburb-level filtration driver');

    return {
      level: level,
      title: level === 'Low' ? 'Filtration relevance: low to moderate' : 'Filtration relevance: worth discussing',
      body: level === 'Low'
        ? 'A filter may still improve taste and convenience, but the sourced suburb data does not suggest a strong network-level issue.'
        : 'A tap filter may be relevant depending on the exact concern, product specification and household plumbing.',
      reasons: reasons
    };
  }

  function resultNumeric(results, parameterId) {
    var result = resultFor(results, parameterId);
    return result && !result.unavailable ? numericValue(result.value) : null;
  }

  function purestProductRecommendations(results, recommendation) {
    var chlorineValue = resultNumeric(results, 'chlorine');
    var turbidityValue = resultNumeric(results, 'turbidity');
    var hardnessValue = resultNumeric(results, 'hardness');
    var leadValue = resultNumeric(results, 'lead');
    var copperValue = resultNumeric(results, 'copper');
    var chlorineFlag = chlorineValue !== null && chlorineValue >= 0.5;
    var clarityFlag = turbidityValue !== null && turbidityValue > 1;
    var scaleFlag = hardnessValue !== null && hardnessValue >= 60;
    var metalsFlag = (leadValue !== null && leadValue > 0.001) || (copperValue !== null && copperValue > 0.05);

    return [
      {
        name: 'Filtered Tap System',
        href: '/products/filtered-tap-system',
        image: {
          src: 'https://cdn.shopify.com/s/files/1/0579/9378/9520/files/Filtered_Tap_2.png?v=1780547951&width=600',
          alt: 'Filtered tap system installed at a kitchen sink'
        },
        badge: chlorineFlag || clarityFlag ? 'Strong everyday fit' : 'Everyday upgrade',
        body: chlorineFlag || clarityFlag
          ? 'Same filtration performance as the pull-out system, paired with a standard filtered tap. Includes a carbon + sediment filter by default to reduce chlorine taste and odour plus sediment and visible particles. Replacement filter: $100.'
          : 'A simple under-sink filtration option with a standard filtered tap. Includes a carbon + sediment filter by default to reduce chlorine taste and odour plus sediment and visible particles. Replacement filter: $100.',
        fit: chlorineFlag ? 'Standard tap plus filtration' : (clarityFlag ? 'Standard tap plus filtration' : 'Standard tap preference')
      },
      {
        name: 'Pull Out Filtered Tap System',
        href: '/products/pull-out-filtered-tap-system',
        image: {
          src: 'https://cdn.shopify.com/s/files/1/0579/9378/9520/files/pull-out-mixer-03-pullout-in-hand.png?width=600',
          alt: 'Pull out filtered mixer tap in use'
        },
        badge: 'Premium tap choice',
        body: 'Uses the same filtration system, but replaces the existing mixer with a premium pull-out tap. Includes a carbon + sediment filter by default to reduce chlorine taste and odour plus sediment and visible particles. Replacement filter: $100.',
        fit: 'Pull-out mixer preference'
      },
      {
        name: 'Reverse Osmosis System',
        href: '/products/reverse-osmosis-system',
        image: {
          src: 'https://cdn.shopify.com/s/files/1/0579/9378/9520/files/SF407808.jpg?v=1766105861&width=600',
          alt: 'Reverse osmosis system installed under a sink'
        },
        badge: metalsFlag ? 'Highest-confidence option' : 'Maximum filtration pathway',
        body: metalsFlag
          ? 'Worth discussing if metals or plumbing are part of the concern. Match any claims to the product specification and installation.'
          : 'For households wanting a stronger filtration pathway than a standard carbon tap filter.',
        fit: metalsFlag ? 'Metals/plumbing context' : (recommendation.level === 'Medium' ? 'Broader peace of mind' : 'Premium drinking water')
      },
      {
        name: 'Commercial Water Filters',
        href: '/collections/commercial',
        images: [
          {
            src: 'https://cdn.shopify.com/s/files/1/0579/9378/9520/files/SF407557_a8f358e0-56ec-4f10-9cd6-46e8b53e18b0.jpg?v=1758200133&width=360',
            alt: 'Plumbed standing water cooler'
          },
          {
            src: 'https://cdn.shopify.com/s/files/1/0579/9378/9520/files/SF407605.jpg?v=1757640729&width=360',
            alt: 'Commercial filtered drinking water bubbler'
          }
        ],
        badge: scaleFlag ? 'Workplace/site fit' : 'Business option',
        body: 'For offices, clinics, schools, gyms and worksites where flow rate, servicing and usage volume matter.',
        fit: scaleFlag ? 'Scale and servicing context' : 'Shared workplace water'
      }
    ];
  }

  function renderProductRecommendations(parent, results, recommendation) {
    var wrap = document.createElement('div');
    wrap.className = 'ps-wc-product-options';

    var heading = document.createElement('div');
    heading.className = 'ps-wc-product-options__heading';
    var title = document.createElement('strong');
    title.textContent = 'Recommended Purest options';
    var note = document.createElement('span');
    note.textContent = 'Matched to the suburb profile and typical tap-water concerns.';
    heading.appendChild(title);
    heading.appendChild(note);
    wrap.appendChild(heading);

    purestProductRecommendations(results, recommendation).forEach(function (product) {
      var row = document.createElement('a');
      row.className = 'ps-wc-product-option';
      row.href = product.href;
      row.addEventListener('click', function () {
        track('water_checker_product_recommendation_clicked', {
          product_name: product.name,
          href: product.href,
          recommendation_level: recommendation ? recommendation.level : null
        });
      });

      var media = document.createElement('span');
      media.className = product.images ? 'ps-wc-product-option__media ps-wc-product-option__media--pair' : 'ps-wc-product-option__media';
      var images = product.images || (product.image ? [product.image] : []);
      images.forEach(function (image) {
        var img = document.createElement('img');
        img.src = image.src;
        img.alt = image.alt;
        img.loading = 'lazy';
        img.width = product.images ? 180 : 220;
        img.height = product.images ? 180 : 220;
        media.appendChild(img);
      });

      var content = document.createElement('span');
      content.className = 'ps-wc-product-option__content';
      var nameLine = document.createElement('span');
      nameLine.className = 'ps-wc-product-option__name';
      nameLine.textContent = product.name;
      var body = document.createElement('span');
      body.className = 'ps-wc-product-option__body';
      body.textContent = product.body;
      content.appendChild(nameLine);
      content.appendChild(body);

      var meta = document.createElement('span');
      meta.className = 'ps-wc-product-option__meta';
      var badge = document.createElement('span');
      badge.className = 'ps-wc-product-option__badge';
      badge.textContent = product.badge;
      var fit = document.createElement('span');
      fit.className = 'ps-wc-product-option__fit';
      fit.textContent = product.fit;
      meta.appendChild(badge);
      meta.appendChild(fit);

      row.appendChild(media);
      row.appendChild(content);
      row.appendChild(meta);
      wrap.appendChild(row);
    });

    parent.appendChild(wrap);
  }

  function appendTextList(parent, items) {
    var list = document.createElement('ul');
    items.forEach(function (item) {
      var li = document.createElement('li');
      li.textContent = item;
      list.appendChild(li);
    });
    parent.appendChild(list);
  }

  function renderDecisionCards(record, provider, zone, results) {
    if (!els.filterMatchCard || !els.providerFirstCard) return;
    clearNode(els.filterMatchCard);
    clearNode(els.providerFirstCard);
    var incidents = incidentsFor(record, provider, zone);
    var recommendation = filterRecommendation(results, incidents.length);

    var eyebrow = document.createElement('p');
    eyebrow.className = 'ps-wc-decision-eyebrow';
    eyebrow.textContent = 'Filter match';
    var title = document.createElement('h3');
    title.textContent = recommendation.title;
    var body = document.createElement('p');
    body.textContent = recommendation.body;
    els.filterMatchCard.appendChild(eyebrow);
    els.filterMatchCard.appendChild(title);
    els.filterMatchCard.appendChild(body);
    appendTextList(els.filterMatchCard, recommendation.reasons);

    var actions = document.createElement('div');
    actions.className = 'ps-wc-decision-actions';
    var enquire = document.createElement('a');
    enquire.href = '/pages/contact';
    enquire.className = 'ps-wc-btn ps-wc-btn--primary';
    enquire.textContent = 'Ask Purest about this';
    var shop = document.createElement('a');
    shop.href = '/collections/all';
    shop.className = 'ps-wc-btn ps-wc-btn--secondary';
    shop.textContent = 'Shop filtration';
    actions.appendChild(enquire);
    actions.appendChild(shop);
    els.filterMatchCard.appendChild(actions);
    renderProductRecommendations(els.filterMatchCard, results, recommendation);

    var providerTitle = document.createElement('h3');
    providerTitle.textContent = 'When to contact your water provider first';
    var providerBody = document.createElement('p');
    providerBody.textContent = 'If the issue is sudden, severe or happening today, check active notices with ' + (provider ? provider.name : 'your water retailer') + ' before treating it as a filtration problem.';
    els.providerFirstCard.appendChild(providerTitle);
    els.providerFirstCard.appendChild(providerBody);
    appendTextList(els.providerFirstCard, ['Boil-water or do-not-drink notices', 'Sudden discolouration, chemical smell, pressure loss or illness concerns', 'Water outages, burst mains or urgent public-health advice']);
    if (provider && provider.website) {
      var link = document.createElement('a');
      link.href = provider.website;
      link.target = '_blank';
      link.rel = 'noopener';
      link.className = 'ps-wc-text-link';
      link.textContent = 'Open provider website';
      link.setAttribute('aria-label', 'Open ' + provider.name + ' website');
      els.providerFirstCard.appendChild(link);
    }
  }

  /* ── Water quality cards ──────────────────────────────── */
  var PARAM_ICONS = {
    'droplet': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 2.5C10 2.5 4.5 9.2 4.5 12.7C4.5 15.9 6.9 17.9 10 17.9C13.1 17.9 15.5 15.9 15.5 12.7C15.5 9.2 10 2.5 10 2.5Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M7.3 13C7.3 14.4 8.3 15.2 9.5 15.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    'shield': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 2.6L16.5 5V9.7C16.5 13.7 13.8 16.7 10 17.8C6.2 16.7 3.5 13.7 3.5 9.7V5L10 2.6Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
    'check-shield': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 2.6L16.5 5V9.7C16.5 13.7 13.8 16.7 10 17.8C6.2 16.7 3.5 13.7 3.5 9.7V5L10 2.6Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M7.3 9.9L9.2 11.8L12.9 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    'scale': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 3V17M6 6H14M6 6L3.8 10.5C3.8 11.7 4.8 12.4 6 12.4C7.2 12.4 8.2 11.7 8.2 10.5L6 6ZM14 6L11.8 10.5C11.8 11.7 12.8 12.4 14 12.4C15.2 12.4 16.2 11.7 16.2 10.5L14 6Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    'wave': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M2.5 8C4 6.3 5.5 6.3 7 8C8.5 9.7 10 9.7 11.5 8C13 6.3 14.5 6.3 16 8C17.5 9.7 17.5 9.7 17.5 9.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.5 13C4 11.3 5.5 11.3 7 13C8.5 14.7 10 14.7 11.5 13C13 11.3 14.5 11.3 16 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    'crystal': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5.5 4H14.5L18 9L10 18L2 9L5.5 4Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M2 9H18M7 4L10 9L10 18M13 4L10 9" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg>',
    'pipe': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M3 7H10C12.2 7 14 8.8 14 11V14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><rect x="2.3" y="4.3" width="5.4" height="5.4" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="11.7" y="11.7" width="5.4" height="5.4" rx="1" stroke="currentColor" stroke-width="1.3"/></svg>',
    'building': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="4" y="2.5" width="9" height="15" rx="0.5" stroke="currentColor" stroke-width="1.3"/><path d="M13 8.5H16.2V17.5H13" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M6.5 5.5H10.5M6.5 8.5H10.5M6.5 11.5H10.5M6.5 14.5H10.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>',
    'pin': '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 18C10 18 15.5 12.6 15.5 8.3C15.5 5.1 13 2.5 10 2.5C7 2.5 4.5 5.1 4.5 8.3C4.5 12.6 10 18 10 18Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><circle cx="10" cy="8.2" r="2.1" stroke="currentColor" stroke-width="1.3"/></svg>'
  };

  function renderQualityBar(node, param, result) {
    var barWrap = node.querySelector('.ps-wc-quality-bar');
    if (typeof param.scale_max !== 'number') return;
    // Only render for a clean single positive number -- never for range-style values like "0.77–0.87"
    if (!/^\d+(\.\d+)?$/.test(String(result.value).trim())) return;
    var numeric = parseFloat(result.value);
    var min = typeof param.scale_min === 'number' ? param.scale_min : 0;
    var max = param.scale_max;
    if (max <= min) return;
    var pct = Math.max(0, Math.min(100, ((numeric - min) / (max - min)) * 100));
    barWrap.hidden = false;
    barWrap.querySelector('.ps-wc-quality-bar-fill').style.width = pct + '%';
    barWrap.querySelector('.ps-wc-quality-bar-dot').style.left = pct + '%';
    barWrap.querySelector('.ps-wc-quality-bar-min').textContent = param.scale_label_min || String(min);
    barWrap.querySelector('.ps-wc-quality-bar-max').textContent = param.scale_label_max || String(max);
  }

  function confidenceForResult(result) {
    if (result.unavailable || result.scope === 'not_published') {
      return {
        label: 'Not published',
        detail: 'No verified value for this parameter is currently included for this suburb or provider.'
      };
    }
    if (result.scope === 'zone') {
      return {
        label: 'Supply-zone reading',
        detail: 'This value is matched to the suburb supply zone where published by the water retailer.'
      };
    }
    if (result.scope === 'system_wide') {
      return {
        label: 'Network-wide reading',
        detail: 'This value is published across the retailer network rather than for this suburb only.'
      };
    }
    return {
      label: 'Verified source',
      detail: 'This value is shown only where a source could be traced.'
    };
  }

  function addQualityBadge(card, result) {
    var confidence = confidenceForResult(result);
    var badge = document.createElement('span');
    badge.className = 'ps-wc-confidence-badge' + (result.scope === 'system_wide' || result.scope === 'not_published' ? ' ps-wc-confidence-badge--soft' : '');
    badge.textContent = confidence.label;
    badge.title = confidence.detail;
    var head = card.querySelector('.ps-wc-quality-head');
    if (head) head.appendChild(badge);
  }

  function qualityPlainStatus(param, result) {
    if (!result || result.unavailable) {
      return {
        tone: 'neutral',
        label: 'No mapped reading',
        body: 'We have not found a verified value for this metric in the sources currently mapped.'
      };
    }

    var value = numericValue(result.value);
    if (result.parameter_id === 'e_coli') {
      if (value === null) return { tone: 'neutral', label: 'Compliance context', body: 'This is reported as a compliance measure rather than a simple concentration.' };
      return value >= 100
        ? { tone: 'good', label: 'Compliant safety indicator', body: 'Reported samples were compliant for E. coli. This is about bacterial safety, not taste.' }
        : { tone: 'watch', label: 'Check official advice', body: 'This is below full compliance in the mapped report, so the original source and current provider advice matter.' };
    }

    if (result.parameter_id === 'chlorine') {
      if (value === null) return { tone: 'neutral', label: 'Taste context', body: 'Chlorine is mainly useful here as taste and odour context.' };
      if (value <= 0.5) return { tone: 'good', label: 'Below health limits', body: 'Health-wise this is low, but chlorine taste or smell can still be noticeable for sensitive households.' };
      return { tone: 'watch', label: 'Taste may be noticeable', body: 'Still below the included health limits, but more likely to be noticed at the tap.' };
    }

    if (result.parameter_id === 'fluoride') {
      if (value === null) return { tone: 'neutral', label: 'Dental-health context', body: 'Fluoride is included for dental-health context, not taste or smell.' };
      if (value >= 0.8 && value <= 1.0) return { tone: 'good', label: 'Around target range', body: 'This sits near the usual fluoridation target range used locally.' };
      return { tone: 'neutral', label: 'Below maximum guide', body: 'This is best read against fluoridation targets and maximum guideline values, not as a taste issue.' };
    }

    if (result.parameter_id === 'ph') {
      if (value === null) return { tone: 'neutral', label: 'Balance context', body: 'pH is useful for treatment and pipe context, but this value is not a simple number to grade.' };
      if (value >= 6.5 && value <= 8.5) return { tone: 'good', label: 'Balanced range', body: 'This is in the normal pH range used for drinking-water balance and corrosion control.' };
      return { tone: 'watch', label: 'Outside typical range', body: 'This is worth reading with the source range because pH affects pipe and treatment context.' };
    }

    if (result.parameter_id === 'turbidity') {
      if (value === null) return { tone: 'neutral', label: 'Clarity context', body: 'Turbidity is useful for clarity context, but this value is not a simple number to grade.' };
      if (value <= 1) return { tone: 'good', label: 'Clear profile', body: 'Low turbidity usually means the monitored supply is very clear.' };
      if (value <= 5) return { tone: 'watch', label: 'Clarity worth checking', body: 'This remains within the guideline shown, but visible particles or cloudiness are worth noting.' };
      return { tone: 'alert', label: 'Check source report', body: 'This sits above the guideline shown and should be checked against official reporting.' };
    }

    if (result.parameter_id === 'hardness') {
      if (value === null) return { tone: 'neutral', label: 'Scale context', body: 'Hardness helps explain scale, but this value is not a simple number to grade.' };
      if (value < 60) return { tone: 'good', label: 'Soft water', body: 'Scale build-up should usually be lower than in hard-water regions.' };
      if (value < 200) return { tone: 'neutral', label: 'Moderate hardness', body: 'Scale may become more noticeable as hardness rises.' };
      return { tone: 'watch', label: 'Scale likely', body: 'Hardness is high enough to be a practical appliance and tapware concern.' };
    }

    if (result.parameter_id === 'copper' || result.parameter_id === 'lead') {
      var label = value !== null && ((result.parameter_id === 'copper' && value <= 0.05) || (result.parameter_id === 'lead' && value <= 0.001))
        ? 'Low network signal'
        : 'Tap plumbing matters';
      return {
        tone: label === 'Low network signal' ? 'good' : 'watch',
        label: label,
        body: 'This network reading is useful context, but your own pipes, fittings and first-draw tap conditions can change the result.'
      };
    }

    return {
      tone: 'neutral',
      label: 'Read with context',
      body: param && param.short_explanation ? param.short_explanation : 'This is a network-level reading, not a direct test of your individual tap.'
    };
  }

  function addQualityPlainStatus(card, param, result) {
    var status = qualityPlainStatus(param, result);
    var node = document.createElement('div');
    node.className = 'ps-wc-quality-plain ps-wc-quality-plain--' + status.tone;
    var label = document.createElement('strong');
    label.textContent = status.label;
    var body = document.createElement('span');
    body.textContent = status.body;
    node.appendChild(label);
    node.appendChild(body);
    var valueNode = card.querySelector('.ps-wc-quality-value');
    if (valueNode && valueNode.nextSibling) {
      card.insertBefore(node, valueNode.nextSibling);
    } else {
      card.appendChild(node);
    }
  }

  function chlorineInsight(result) {
    var value = numericValue(result.value);
    if (value === null) return null;
    var parts = [];
    if (value <= 4) {
      parts.push('This reported average sits below the Australian, WHO, US EPA and New Zealand chlorine limits included here.');
    } else if (value <= 5) {
      parts.push('This reported average sits below the Australian, WHO and New Zealand chlorine limits included here, but above the US EPA residual disinfectant limit.');
    } else {
      parts.push('This reported average is above at least one health-based chlorine benchmark included here.');
    }
    if (value > 0.5) {
      parts.push('It is also above the UK typical operating target of 0.5 mg/L, which may help explain a stronger chlorine taste or smell for some households.');
    } else {
      parts.push('It is around or below the UK typical operating target of 0.5 mg/L, although sensitive people can still notice chlorine below health-guideline levels.');
    }
    return parts.join(' ');
  }

  function parameterInsight(param, result) {
    var value = numericValue(result.value);
    if (result.parameter_id === 'chlorine') return chlorineInsight(result);
    if (value === null) return null;

    if (result.parameter_id === 'fluoride') {
      if (value >= 0.8 && value <= 1.0) return 'This is within the usual optimal fluoridation range used in Melbourne. Fluoride is about dental health, not taste.';
      if (value < 0.8) return 'This is slightly below the usual optimal fluoridation target range, but still well below the maximum guideline shown for drinking water.';
      return 'This is above the usual optimal fluoridation target range. Check the source range and guideline if you want the full context.';
    }

    if (result.parameter_id === 'ph') {
      if (value >= 6.5 && value <= 8.5) return 'This sits in the normal drinking-water pH range. pH mainly affects corrosion control, pipe protection and taste, not whether water looks clear.';
      return 'This sits outside the usual pH guide range shown here, so the source details are worth checking carefully.';
    }

    if (result.parameter_id === 'turbidity') {
      if (value <= 1) return 'This is a low turbidity reading, which usually means the water is very clear at the point monitored by the retailer.';
      if (value <= 5) return 'This remains within the guideline shown here. Higher turbidity can make water look less clear and can matter for treatment effectiveness.';
      return 'This is above the guideline shown here and should be checked against the original authority report.';
    }

    if (result.parameter_id === 'hardness') {
      if (value < 60) return 'This is soft water. In everyday terms, scale build-up around kettles, taps and appliances should usually be lower than in hard-water regions.';
      if (value < 200) return 'This is moderately hard water, still below the aesthetic guide shown here. Scale around taps or kettles can become more noticeable as hardness rises.';
      return 'This is above the aesthetic hardness guide shown here and may be noticeable through scale build-up.';
    }

    if (result.parameter_id === 'e_coli') {
      if (value >= 100) return 'This indicates the samples reported for this area were compliant for E. coli. E. coli is a safety indicator, not a taste or smell measure.';
      return 'This is below full compliance in the reported samples. Check the source report and any current provider advice.';
    }

    if (result.parameter_id === 'copper') {
      if (value <= 0.05) return 'This is very low compared with the health guideline shown here. Copper at the tap can still be influenced by your own household pipework and fittings.';
      if (value <= 2) return 'This is below the health guideline shown here. If you notice metallic taste, your home plumbing may still be part of the story.';
      return 'This is above the health guideline shown here and should be checked against the source report.';
    }

    if (result.parameter_id === 'lead') {
      if (value <= 0.001) return 'This is very low compared with the health guideline shown here. Lead risk is often more about old internal plumbing and fittings than the mains supply.';
      if (value <= 0.01) return 'This is below the health guideline shown here, but older fittings and internal plumbing can still affect what comes out of a specific tap.';
      return 'This is above the health guideline shown here and should be checked against the source report.';
    }

    if (param && param.short_explanation) return param.short_explanation;
    return null;
  }

  function addInsight(card, result) {
    var param = byId(DATA.parameters || [], result.parameter_id);
    var text = parameterInsight(param, result);
    if (!text) return;
    var insight = document.createElement('div');
    insight.className = 'ps-wc-insight';
    var label = document.createElement('strong');
    label.textContent = 'What this means';
    var body = document.createElement('p');
    body.textContent = text;
    insight.appendChild(label);
    insight.appendChild(body);
    var sourceNode = card.querySelector('.ps-wc-quality-source');
    card.insertBefore(insight, sourceNode || null);
  }

  function addQualityCta(card, result) {
    var messages = {
      chlorine: 'Concerned about chlorine taste or smell? Ask which carbon cartridge suits your home.',
      turbidity: 'Seeing sediment or cloudiness? Check active provider notices first, then ask about filtration options.',
      hardness: 'Seeing scale around taps or kettles? Compare the hardness reading before choosing a system.',
      copper: 'Metallic taste can involve internal plumbing. Ask about tap-specific next steps.',
      lead: 'Older fittings can matter. Ask us about tap-specific testing and filtration evidence.'
    };
    var text = messages[result.parameter_id];
    if (!text) return;
    var cta = document.createElement('div');
    cta.className = 'ps-wc-quality-cta';
    var copy = document.createElement('p');
    copy.textContent = text;
    var link = document.createElement('a');
    link.href = '/pages/contact';
    link.textContent = 'Ask Purest';
    cta.appendChild(copy);
    cta.appendChild(link);
    var sourceNode = card.querySelector('.ps-wc-quality-source');
    card.insertBefore(cta, sourceNode || null);
  }

  function renderSourceDrawer(card, result, benchmarks) {
    var hasResultSource = result.source_url || result.source_publication || result.date_retrieved;
    var hasGuidelines = benchmarks && benchmarks.length;
    if (!hasResultSource && !hasGuidelines) return;

    var details = document.createElement('details');
    details.className = 'ps-wc-source-drawer';
    var summary = document.createElement('summary');
    summary.textContent = 'View sources';
    details.appendChild(summary);

    var list = document.createElement('div');
    list.className = 'ps-wc-source-list';

    if (hasResultSource) {
      var resultItem = document.createElement('p');
      var resultLabel = result.source_org || 'Water authority';
      if (result.source_url) {
        var resultLink = document.createElement('a');
        resultLink.href = result.source_url;
        resultLink.target = '_blank';
        resultLink.rel = 'noopener';
        resultLink.textContent = resultLabel;
        resultItem.appendChild(resultLink);
      } else {
        resultItem.appendChild(document.createTextNode(resultLabel));
      }
      var publication = [];
      if (result.source_publication) publication.push(result.source_publication);
      if (result.reporting_period) publication.push(result.reporting_period);
      if (result.date_retrieved) publication.push('retrieved ' + result.date_retrieved);
      if (publication.length) resultItem.appendChild(document.createTextNode(' - ' + publication.join(', ')));
      list.appendChild(resultItem);
    }

    (benchmarks || []).forEach(function (benchmark) {
      if (!benchmark.source_url) return;
      var item = document.createElement('p');
      var link = document.createElement('a');
      link.href = benchmark.source_url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = benchmark.jurisdiction + ' - ' + (benchmark.source_label || benchmark.agency);
      item.appendChild(link);
      if (benchmark.last_verified) item.appendChild(document.createTextNode(' - verified ' + benchmark.last_verified));
      list.appendChild(item);
    });

    details.appendChild(list);
    var sourceNode = card.querySelector('.ps-wc-quality-source');
    card.insertBefore(details, sourceNode || null);
  }

  function guidelineStatus(value, benchmark) {
    if (value === null) return 'Reference';
    if (benchmark.comparison_operator === 'range') {
      if (typeof benchmark.comparison_min !== 'number' || typeof benchmark.comparison_max !== 'number') return 'Reference';
      return value >= benchmark.comparison_min && value <= benchmark.comparison_max ? 'Within range' : 'Outside range';
    }
    if (typeof benchmark.comparison_value !== 'number') return 'Reference';
    if (benchmark.comparison_operator === 'typical_lte') {
      return value <= benchmark.comparison_value ? 'Within typical target' : 'Above typical target';
    }
    if (benchmark.comparison_operator === 'within_or_below') {
      return value <= benchmark.comparison_value ? 'Within range' : 'Above range';
    }
    if (benchmark.comparison_operator === 'lte') {
      return value <= benchmark.comparison_value ? 'Below limit' : 'Above limit';
    }
    return 'Reference';
  }

  function guidelineNote(parameterId) {
    var notes = {
      chlorine: 'Chlorine limits are not the same as taste thresholds. Melbourne readings can be well below health limits while still being noticeable by taste or smell.',
      fluoride: 'Fluoride has both dental-health target ranges and maximum guideline values. This comparison is about official drinking-water references, not whether fluoridation is personally preferred.',
      ph: 'pH ranges are usually operational or aesthetic guidance for corrosion control, treatment performance and taste, not a direct health limit at normal drinking-water values.',
      turbidity: 'Turbidity rules are often treatment-performance rules rather than a simple chemical limit. Lower turbidity generally supports clearer water and more reliable disinfection.',
      e_coli: 'E. coli is normally assessed as presence/absence or compliance, not as a concentration to rank internationally. Detection in treated drinking water requires official investigation.',
      hardness: 'Hardness is mainly aesthetic and appliance context. Most authorities do not set a health limit because calcium and magnesium in drinking water are not usually health concerns.',
      copper: 'Copper and lead can be strongly influenced by household plumbing and tapware. Network-level averages are useful context, but property-specific certainty needs tap sampling.',
      lead: 'Lead can be strongly influenced by household plumbing and tapware. Network-level averages are useful context, but property-specific certainty needs tap sampling.'
    };
    return notes[parameterId] || 'Benchmarks differ by jurisdiction and may be health-based, aesthetic, operational or treatment-performance references.';
  }

  function renderGuidelineComparison(card, result) {
    var benchmarks = (DATA.international_guidelines || []).filter(function (benchmark) {
      return benchmark.parameter_id === result.parameter_id;
    });
    if (!benchmarks.length) {
      renderSourceDrawer(card, result, benchmarks);
      return;
    }

    var value = numericValue(result.value);
    var wrap = document.createElement('details');
    wrap.className = 'ps-wc-guideline-compare';
    var summary = document.createElement('summary');
    summary.textContent = 'Compare internationally';
    wrap.appendChild(summary);

    var heading = document.createElement('div');
    heading.className = 'ps-wc-guideline-heading';
    var title = document.createElement('strong');
    title.textContent = 'Official reference context';
    var meta = document.createElement('span');
    meta.textContent = value === null ? 'Official references' : 'Your reported average vs official references';
    heading.appendChild(title);
    heading.appendChild(meta);
    wrap.appendChild(heading);

    var note = document.createElement('p');
    note.className = 'ps-wc-guideline-note';
    note.textContent = guidelineNote(result.parameter_id);
    wrap.appendChild(note);

    var rows = document.createElement('div');
    rows.className = 'ps-wc-guideline-rows';

    benchmarks.forEach(function (benchmark) {
      var row = document.createElement('div');
      row.className = 'ps-wc-guideline-row';

      var top = document.createElement('div');
      top.className = 'ps-wc-guideline-row-top';
      var label = document.createElement('span');
      label.textContent = benchmark.jurisdiction;
      var valueEl = document.createElement('strong');
      valueEl.textContent = benchmark.value_display;
      top.appendChild(label);
      top.appendChild(valueEl);

      var sub = document.createElement('div');
      sub.className = 'ps-wc-guideline-row-sub';
      var agency = document.createElement('span');
      agency.textContent = benchmark.benchmark_type + ' - ' + benchmark.agency;
      var status = document.createElement('span');
      status.className = 'ps-wc-guideline-status';
      status.textContent = guidelineStatus(value, benchmark);
      sub.appendChild(agency);
      sub.appendChild(status);

      row.appendChild(top);
      row.appendChild(sub);

      if (value !== null && typeof benchmark.comparison_value === 'number') {
        var track = document.createElement('div');
        track.className = 'ps-wc-guideline-track';
        var benchmarkPct = 100;
        var compareMax = benchmark.comparison_operator === 'range' && typeof benchmark.comparison_max === 'number'
          ? benchmark.comparison_max
          : benchmark.comparison_value;
        var markerMax = Math.max(compareMax, value, 1);
        var markerPct = Math.max(0, Math.min(100, (value / markerMax) * 100));
        if (markerMax > compareMax) {
          benchmarkPct = Math.max(0, Math.min(100, (compareMax / markerMax) * 100));
        }
        var limit = document.createElement('span');
        limit.className = 'ps-wc-guideline-limit';
        limit.style.left = benchmarkPct + '%';
        var marker = document.createElement('span');
        marker.className = 'ps-wc-guideline-marker';
        marker.style.left = markerPct + '%';
        marker.setAttribute('aria-label', 'Reported value ' + result.value + ' ' + (result.unit || ''));
        track.appendChild(limit);
        track.appendChild(marker);
        row.appendChild(track);
      }

      rows.appendChild(row);
    });

    wrap.appendChild(rows);
    var sourceNode = card.querySelector('.ps-wc-quality-source');
    card.insertBefore(wrap, sourceNode || null);
    renderSourceDrawer(card, result, benchmarks);
  }

  function renderQuality(zone, provider) {
    clearNode(els.qualityGrid);
    var results = displayResultsForZone(zone, provider);

    if (!results.length) {
      var note = document.createElement('p');
      note.className = 'ps-wc-quality-note';
      note.textContent = 'Detailed water-quality figures for this supply zone aren’t published in a form we can verify yet. We only show figures we can trace back to an official source, so we’ve left this section out for now — check back as we expand this dataset.';
      els.qualityGrid.appendChild(note);
      return;
    }

    results.forEach(function (result) {
      var param = byId(DATA.parameters, result.parameter_id);
      if (!param) return;
      var node = els.tplQualityCard.content.cloneNode(true);
      var card = node.querySelector('.ps-wc-quality-card');
      card.classList.add('ps-wc-quality-card--' + result.parameter_id);
      if (result.unavailable) card.classList.add('is-unavailable');
      if (result.scope === 'system_wide') card.classList.add('is-network-wide');
      var iconEl = node.querySelector('.ps-wc-quality-icon');
      if (param.icon && PARAM_ICONS[param.icon]) iconEl.innerHTML = PARAM_ICONS[param.icon];
      node.querySelector('.ps-wc-quality-name').textContent = param.name;
      addQualityBadge(card, result);
      node.querySelector('.ps-wc-quality-value strong').textContent = result.value;
      node.querySelector('.ps-wc-quality-value span').textContent = result.unit || param.unit || '';
      addQualityPlainStatus(card, param, result);
      node.querySelector('.ps-wc-quality-context').textContent = result.unavailable
        ? 'This parameter is part of the profile, but a verified value for this suburb is not published in the sourced reports currently included.'
        : (param.short_explanation || '');

      if (!result.unavailable) {
        renderQualityBar(node, param, result);
        addInsight(card, result);
        addQualityCta(card, result);
        renderGuidelineComparison(card, result);
      }

      var learnParts = [];
      if (param.long_explanation) learnParts.push(param.long_explanation);
      if (result.unavailable) {
        learnParts.push('We do not estimate missing readings. A missing value means it is not available in the sources currently mapped into this tool.');
      } else {
        if (!isPending(result.range)) learnParts.push('Reported ' + result.range + '.');
        if (!isPending(result.guideline)) learnParts.push('Guideline: ' + result.guideline + '.');
      }
      node.querySelector('.ps-wc-learn-body').textContent = learnParts.join(' ');

      var sourceBits = [];
      if (result.source_org) sourceBits.push(result.source_org);
      if (result.reporting_period) sourceBits.push(result.reporting_period);
      var sourceLine = sourceBits.length ? 'Source: ' + sourceBits.join(', ') : '';
      if (result.unavailable) sourceLine = provider ? 'No sourced value currently mapped for ' + provider.name + '.' : 'No sourced value currently mapped.';
      if (result.scope === 'system_wide') sourceLine += (sourceLine ? ' - ' : '') + 'network-wide average, not specific to this suburb';
      node.querySelector('.ps-wc-quality-source').textContent = sourceLine;

      var learnBtn = node.querySelector('.ps-wc-learn-more');
      var learnBody = node.querySelector('.ps-wc-learn-body');
      learnBtn.addEventListener('click', function () {
        var open = learnBody.classList.toggle('is-open');
        learnBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      els.qualityGrid.appendChild(node);
    });
  }

  /* ── History timeline ─────────────────────────────────── */
  function incidentMatches(incident, record, provider, zone) {
    var suburbHit = (incident.affected_suburbs || []).some(function (s) { return normalize(s) === normalize(record.suburb); });
    var postcodeHit = (incident.affected_postcodes || []).indexOf(record.postcode) !== -1;
    var providerHit = provider && incident.provider_id === provider.id && incident.geographic_scope === 'provider_wide';
    var zoneHit = zone && incident.zone_id === zone.id;
    return suburbHit || postcodeHit || providerHit || zoneHit;
  }

  function formatIncidentDate(incident) {
    if (isPending(incident.date_started)) return '';
    var d = new Date(incident.date_started);
    if (isNaN(d.getTime())) return incident.date_started;
    return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  }

  function renderHistory(record, provider, zone) {
    clearNode(els.historyTimeline);
    var incidents = (DATA.incidents || []).filter(function (incident) {
      return incidentMatches(incident, record, provider, zone) && !isPending(incident.summary);
    });

    if (!incidents.length) {
      setHidden(els.historySection, true);
      return;
    }
    setHidden(els.historySection, false);
    els.historyTimeline.classList.remove('is-empty');

    incidents.forEach(function (incident) {
      var node = els.tplHistoryEvent.content.cloneNode(true);
      node.querySelector('.ps-wc-history-event').setAttribute('data-status', incident.status || '');
      node.querySelector('.ps-wc-history-date').textContent = formatIncidentDate(incident);
      node.querySelector('.ps-wc-history-title').textContent = incident.title;
      node.querySelector('.ps-wc-history-summary').textContent = incident.summary;

      var badges = node.querySelector('.ps-wc-history-badges');
      badges.appendChild(makeChip('Historical event', 'historical'));
      if (incident.status === 'resolved') badges.appendChild(makeChip('Resolved', 'resolved'));
      else if (incident.status) badges.appendChild(makeChip(capitalize(incident.status), 'current'));

      var detail = node.querySelector('.ps-wc-history-detail');
      var learnBtn = node.querySelector('.ps-wc-learn-more');
      if (!isPending(incident.detail)) {
        detail.textContent = incident.detail;
        learnBtn.addEventListener('click', function () {
          var open = detail.classList.toggle('is-open');
          learnBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) track('history_event_opened', { incident_id: incident.id });
        });
      } else {
        learnBtn.remove();
      }

      var meta = node.querySelector('.ps-wc-history-meta');
      if (incident.source_org) {
        var srcSpan = document.createElement('span');
        srcSpan.textContent = 'Source: ' + incident.source_org;
        meta.appendChild(srcSpan);
      }
      if (!isPending(incident.source_url)) {
        var a = document.createElement('a');
        a.href = incident.source_url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = 'Read original notice →';
        meta.appendChild(a);
      }

      els.historyTimeline.appendChild(node);
    });
  }

  function makeChip(label, kind) {
    var span = document.createElement('span');
    span.className = 'ps-wc-chip ps-wc-chip--' + kind;
    span.textContent = label;
    return span;
  }

  function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s; }

  /* ── CTA local facts strip (real numbers for this suburb, not marketing copy) ── */
  function renderCtaFacts(record, provider, zone) {
    if (!els.ctaFacts) return;
    clearNode(els.ctaFacts);

    var qualityCount = qualityResultsForZone(zone).length;
    var incidentCount = (DATA.incidents || []).filter(function (i) {
      return incidentMatches(i, record, provider, zone) && !isPending(i.summary);
    }).length;

    var facts = [
      { value: provider ? provider.name : 'Not yet confirmed', label: 'Your water provider' },
      { value: String(qualityCount), label: qualityCount === 1 ? 'verified reading for your zone' : 'verified readings for your zone' },
      { value: String(incidentCount), label: incidentCount === 1 ? 'historical incident on record' : 'historical incidents on record' }
    ];

    facts.forEach(function (fact) {
      var item = document.createElement('div');
      item.className = 'ps-wc-cta-fact';
      var v = document.createElement('strong');
      v.textContent = fact.value;
      var l = document.createElement('span');
      l.textContent = fact.label;
      item.appendChild(v);
      item.appendChild(l);
      els.ctaFacts.appendChild(item);
    });
  }

  /* ── Property questionnaire ───────────────────────────── */
  var propertyStarted = false;

  var PROPERTY_OPTION_META = {
    observations: {
      chlorine_taste: { icon: 'droplet', detail: 'Often a taste and odour question.' },
      metallic_taste: { icon: 'pipe', detail: 'Can involve tapware or plumbing.' },
      sediment: { icon: 'wave', detail: 'Particles, grit or visible specks.' },
      cloudiness: { icon: 'wave', detail: 'Clarity signal worth matching.' },
      discolouration: { icon: 'droplet', detail: 'Check if sudden or recurring.' },
      scale: { icon: 'crystal', detail: 'Usually linked to hardness.' },
      smell: { icon: 'droplet', detail: 'Taste and odour pathway.' },
      nothing: { icon: 'check-shield', detail: 'Use the suburb profile only.' },
      other: { icon: 'pin', detail: 'We can talk through it.' }
    },
    priorities: {
      taste: { icon: 'droplet', detail: 'Everyday drinking water.' },
      chlorine: { icon: 'droplet', detail: 'Carbon filtration discussion.' },
      sediment: { icon: 'wave', detail: 'Particle and clarity focus.' },
      contaminants: { icon: 'shield', detail: 'Match to certified claims.' },
      family: { icon: 'shield', detail: 'Extra peace of mind.' },
      peace_of_mind: { icon: 'check-shield', detail: 'Confidence and convenience.' },
      appliances: { icon: 'crystal', detail: 'Scale and fittings.' },
      other: { icon: 'pin', detail: 'Something specific.' }
    },
    home_age: {
      under_10: { detail: 'Newer home' },
      '10_30': { detail: 'Established home' },
      '30_50': { detail: 'Older fittings possible' },
      over_50: { detail: 'Plumbing context matters' },
      not_sure: { detail: 'Keep it cautious' }
    },
    pipes_replaced: {
      yes: { detail: 'Lower plumbing uncertainty' },
      no: { detail: 'Tap-specific context matters' },
      not_sure: { detail: 'Worth checking if concerned' }
    }
  };

  function questionById(id) {
    var pq = DATA.property_questions;
    return pq && pq.questions ? byId(pq.questions, id) : null;
  }

  function questionHeading(id, fallback) {
    if (id === 'observations') return '1. Pick what you notice';
    if (id === 'home_age') return '2. Add quick home context';
    if (id === 'pipes_replaced') return 'Pipe and tapware certainty';
    if (id === 'priorities') return '3. Choose what you want improved';
    return fallback;
  }

  function renderQuestion(q) {
    var wrap = document.createElement('div');
    wrap.className = 'ps-wc-q ps-wc-q--' + q.id;
    var label = document.createElement('span');
    label.className = 'ps-wc-q-label';
    label.textContent = questionHeading(q.id, q.label);
    wrap.appendChild(label);

    if (q.id === 'observations') {
      var hint = document.createElement('p');
      hint.className = 'ps-wc-q-hint';
      hint.textContent = 'Start with the thing that made you search. You can choose more than one.';
      wrap.appendChild(hint);
    }

    var optionsWrap = document.createElement('div');
    optionsWrap.className = 'ps-wc-options ps-wc-options--' + (q.id === 'observations' ? 'cards' : (q.id === 'home_age' || q.id === 'pipes_replaced' ? 'compact' : 'chips'));
    optionsWrap.setAttribute('role', q.type === 'single' ? 'radiogroup' : 'group');
    optionsWrap.setAttribute('aria-label', q.label);

    q.options.forEach(function (opt, i) {
      var optWrap = document.createElement('span');
      optWrap.className = 'ps-wc-option ps-wc-option--' + q.id;
      var input = document.createElement('input');
      input.type = q.type === 'single' ? 'radio' : 'checkbox';
      input.name = 'pq-' + q.id;
      input.id = 'pq-' + q.id + '-' + i;
      input.value = opt.value;
      input.addEventListener('change', function () {
        if (!propertyStarted) { propertyStarted = true; track('property_questions_started', {}); }
      });
      var lbl = document.createElement('label');
      lbl.setAttribute('for', input.id);
      var meta = PROPERTY_OPTION_META[q.id] && PROPERTY_OPTION_META[q.id][opt.value] ? PROPERTY_OPTION_META[q.id][opt.value] : {};
      if (q.id === 'observations' || q.id === 'priorities') {
        var icon = document.createElement('span');
        icon.className = 'ps-wc-option-icon';
        icon.innerHTML = PARAM_ICONS[meta.icon] || PARAM_ICONS.pin || '';
        lbl.appendChild(icon);
      }
      var text = document.createElement('span');
      text.className = 'ps-wc-option-text';
      var title = document.createElement('strong');
      title.textContent = opt.label;
      text.appendChild(title);
      if (meta.detail) {
        var detail = document.createElement('small');
        detail.textContent = meta.detail;
        text.appendChild(detail);
      }
      lbl.appendChild(text);
      optWrap.appendChild(input);
      optWrap.appendChild(lbl);
      optionsWrap.appendChild(optWrap);
    });

    wrap.appendChild(optionsWrap);
    return wrap;
  }

  function renderPropertyForm() {
    clearNode(els.propertyForm);
    propertyStarted = false;
    var pq = DATA.property_questions;
    if (!pq || !pq.questions) return;

    var homeRow = document.createElement('div');
    homeRow.className = 'ps-wc-home-context-row';

    ['observations', 'home_age', 'pipes_replaced', 'priorities'].forEach(function (id) {
      var q = questionById(id);
      if (!q) return;
      if (id === 'home_age' || id === 'pipes_replaced') {
        homeRow.appendChild(renderQuestion(q));
        if (id === 'pipes_replaced') els.propertyForm.appendChild(homeRow);
      } else {
        els.propertyForm.appendChild(renderQuestion(q));
      }
    });
  }

  function collectPropertyAnswers() {
    var pq = DATA.property_questions;
    var answers = {};
    if (!pq || !pq.questions) return answers;
    pq.questions.forEach(function (q) {
      if (q.type === 'single') {
        var checked = els.propertyForm.querySelector('input[name="pq-' + q.id + '"]:checked');
        answers[q.id] = checked ? checked.value : null;
      } else {
        var boxes = els.propertyForm.querySelectorAll('input[name="pq-' + q.id + '"]:checked');
        answers[q.id] = Array.prototype.map.call(boxes, function (b) { return b.value; });
      }
    });
    return answers;
  }

  function optionLabel(question, value) {
    var opt = question.options.filter(function (o) { return o.value === value; })[0];
    return opt ? opt.label : value;
  }

  if (els.propertySubmit) {
    els.propertySubmit.addEventListener('click', function () {
      var answers = collectPropertyAnswers();
      track('property_questions_completed', {});
      buildSummary(answers);
    });
  }

  if (els.propertySkip) {
    els.propertySkip.addEventListener('click', function () {
      buildSummary(null);
    });
  }

  /* ── Personalised summary ─────────────────────────────── */
  function summaryBlock(title, paragraphs, className) {
    var block = document.createElement('div');
    block.className = 'ps-wc-summary-block' + (className ? ' ' + className : '');
    var h = document.createElement('h3');
    h.textContent = title;
    block.appendChild(h);
    paragraphs.forEach(function (text) {
      var p = document.createElement('p');
      p.textContent = text;
      block.appendChild(p);
    });
    return block;
  }

  function appendSummaryList(block, items) {
    var ul = document.createElement('ul');
    items.forEach(function (text) {
      if (!text) return;
      var li = document.createElement('li');
      li.textContent = text;
      ul.appendChild(li);
    });
    block.appendChild(ul);
  }

  function resultFor(results, parameterId) {
    return results.filter(function (r) { return r.parameter_id === parameterId; })[0] || null;
  }

  function shortValue(result) {
    return result ? result.value + (result.unit ? ' ' + result.unit : '') : null;
  }

  function answerHas(answers, key, values) {
    if (!answers || !answers[key]) return false;
    var current = Array.isArray(answers[key]) ? answers[key] : [answers[key]];
    return values.some(function (value) { return current.indexOf(value) !== -1; });
  }

  function selectedLabels(question, values) {
    if (!question || !values || !values.length) return [];
    return values.filter(function (v) { return v !== 'nothing'; }).map(function (v) { return optionLabel(question, v); });
  }

  function summaryActions(answers, qualityResults) {
    var actions = [];
    var chlorine = resultFor(qualityResults, 'chlorine');
    var hardness = resultFor(qualityResults, 'hardness');
    var lead = resultFor(qualityResults, 'lead');
    var copper = resultFor(qualityResults, 'copper');

    if (answerHas(answers, 'observations', ['chlorine_taste', 'smell']) || answerHas(answers, 'priorities', ['chlorine', 'taste'])) {
      actions.push('For chlorine taste or smell, a suitable carbon filter may help improve taste and odour. This is not a claim that the mains water is unsafe.');
    }
    if (answerHas(answers, 'observations', ['metallic_taste']) || answerHas(answers, 'priorities', ['contaminants'])) {
      actions.push('For metallic taste or contaminant concerns, look at both the authority readings and your home plumbing age. Old fittings can affect tap water after it leaves the mains.');
    }
    if (answerHas(answers, 'observations', ['sediment', 'cloudiness', 'discolouration']) || answerHas(answers, 'priorities', ['sediment'])) {
      actions.push('For sediment, cloudiness or discolouration, check whether it clears after running the cold tap. If it keeps happening or appears suddenly, contact your water retailer first; it may relate to local works, network conditions or internal plumbing.');
    }
    if (answerHas(answers, 'observations', ['scale']) || answerHas(answers, 'priorities', ['appliances'])) {
      actions.push('For scale around taps or kettles, hardness is the key reading. Your reported hardness is ' + (hardness ? shortValue(hardness) : 'not available for this zone') + '.');
    }
    if (answerHas(answers, 'priorities', ['family'])) {
      actions.push('For children or family drinking water, pay closest attention to E. coli compliance, lead/copper context and the condition of your own tapware and internal plumbing.');
    }

    if (!actions.length) {
      actions.push('Use the snapshot above as a local baseline, then focus on what you actually notice at the tap: taste, smell, visible particles, scale or metallic notes.');
      if (chlorine) actions.push('Your reported chlorine average is ' + shortValue(chlorine) + ', which is usually more relevant to taste and smell than visual clarity.');
    }

    if ((lead && numericValue(lead.value) > 0.001) || (copper && numericValue(copper.value) > 0.05)) {
      actions.push('Because metals can be influenced by household plumbing, a first-draw tap sample is more informative than suburb-level network data if you want property-specific certainty.');
    }

    return actions;
  }

  function selectedValues(answers, key) {
    if (!answers || !answers[key]) return [];
    return Array.isArray(answers[key]) ? answers[key] : [answers[key]];
  }

  function selectedOptionLabelsById(id, values) {
    var q = questionById(id);
    return selectedLabels(q, values);
  }

  function tapMatchDiagnosis(answers, qualityResults) {
    var observations = selectedValues(answers, 'observations');
    var priorities = selectedValues(answers, 'priorities');
    var chlorine = resultFor(qualityResults, 'chlorine');
    var turbidity = resultFor(qualityResults, 'turbidity');
    var hardness = resultFor(qualityResults, 'hardness');
    var chlorineValue = chlorine ? numericValue(chlorine.value) : null;
    var turbidityValue = turbidity ? numericValue(turbidity.value) : null;
    var hardnessValue = hardness ? numericValue(hardness.value) : null;

    if (observations.indexOf('sediment') !== -1 || observations.indexOf('cloudiness') !== -1 || observations.indexOf('discolouration') !== -1 || priorities.indexOf('sediment') !== -1 || (turbidityValue !== null && turbidityValue > 1)) {
      return {
        label: 'Clarity and sediment',
        title: 'Your tap check points to clarity first',
        body: 'The useful next step is checking what you see at the tap against the local turbidity and sediment context. Filtration can help with particles, but sudden or severe discolouration should be checked with the water provider first.',
        productNote: 'Start with the Filtered Tap System or Pull Out Filtered Tap System for everyday filtered drinking water.'
      };
    }

    if (observations.indexOf('chlorine_taste') !== -1 || observations.indexOf('smell') !== -1 || priorities.indexOf('chlorine') !== -1 || priorities.indexOf('taste') !== -1 || (chlorineValue !== null && chlorineValue > 0.3)) {
      return {
        label: 'Taste and smell',
        title: 'Your tap check points to taste and odour',
        body: 'Chlorine can be noticeable even when authority readings sit below health guideline values. A carbon-filtration conversation is relevant if taste or smell is what you notice day to day.',
        productNote: 'The tap choice comes down to preference: standard filtered tap or premium pull-out mixer.'
      };
    }

    if (observations.indexOf('metallic_taste') !== -1 || priorities.indexOf('contaminants') !== -1 || answers && (answers.home_age === '30_50' || answers.home_age === 'over_50' || answers.pipes_replaced === 'no' || answers.pipes_replaced === 'not_sure')) {
      return {
        label: 'Tap and plumbing',
        title: 'Your home setup deserves attention',
        body: 'Authority readings describe the broader supply, but metallic taste and older or uncertain plumbing can change what reaches a specific kitchen tap. Product claims should be matched carefully to the concern.',
        productNote: 'Ask us whether a standard carbon system or reverse osmosis pathway makes more sense for the concern.'
      };
    }

    if (observations.indexOf('scale') !== -1 || priorities.indexOf('appliances') !== -1 || (hardnessValue !== null && hardnessValue >= 60)) {
      return {
        label: 'Scale and fittings',
        title: 'Your tap check points to scale context',
        body: 'Hardness is the reading to watch for scale around taps, kettles and appliances. Melbourne areas are often soft, but your selected concern still helps guide the conversation.',
        productNote: 'We can match the product to taste, scale context and how you use the kitchen tap.'
      };
    }

    return {
      label: 'Everyday improvement',
      title: 'Your tap check is a good baseline',
      body: 'No single red flag stands out from your selections. The best next step is improving everyday drinking-water taste, convenience and confidence at the point of use.',
      productNote: 'A filtered tap system is usually the simplest everyday upgrade.'
    };
  }

  function appendSummaryMetric(parent, label, value, detail) {
    var item = document.createElement('div');
    item.className = 'ps-wc-summary-metric';
    var l = document.createElement('span');
    l.textContent = label;
    var v = document.createElement('strong');
    v.textContent = value || 'Not published';
    var d = document.createElement('p');
    d.textContent = detail;
    item.appendChild(l);
    item.appendChild(v);
    item.appendChild(d);
    parent.appendChild(item);
  }

  function summaryProofCard(title, body, label) {
    var card = document.createElement('div');
    card.className = 'ps-wc-summary-proof';
    var eyebrow = document.createElement('span');
    eyebrow.textContent = label;
    var h = document.createElement('h3');
    h.textContent = title;
    var p = document.createElement('p');
    p.textContent = body;
    card.appendChild(eyebrow);
    card.appendChild(h);
    card.appendChild(p);
    return card;
  }

  function matchedRecommendationForAnswers(answers, qualityResults, incidentCount) {
    var recommendation = filterRecommendation(qualityResults, incidentCount);
    var reasons = recommendation.reasons.slice();
    if (answerHas(answers, 'observations', ['chlorine_taste', 'smell']) || answerHas(answers, 'priorities', ['chlorine', 'taste'])) {
      reasons.unshift('your selected tap signal is taste or odour');
    }
    if (answerHas(answers, 'observations', ['sediment', 'cloudiness', 'discolouration']) || answerHas(answers, 'priorities', ['sediment'])) {
      reasons.unshift('your selected tap signal is clarity, sediment or discolouration');
    }
    if (answerHas(answers, 'observations', ['metallic_taste']) || answerHas(answers, 'priorities', ['contaminants'])) {
      reasons.unshift('your selected concern may involve tap-specific plumbing or certified reduction claims');
    }
    return {
      level: reasons.length > recommendation.reasons.length ? 'Medium' : recommendation.level,
      title: recommendation.title,
      body: recommendation.body,
      reasons: reasons
    };
  }

  function buildSummary(answers) {
    if (!activeSuburb) return;
    clearNode(els.summaryBlocks);

    var provider = providerFor(activeSuburb);
    var zone = zoneFor(activeSuburb);
    var qualityResults = qualityResultsForZone(zone);
    var qualityCount = qualityResults.length;
    var incidentCount = (DATA.incidents || []).filter(function (i) { return incidentMatches(i, activeSuburb, provider, zone) && !isPending(i.summary); }).length;
    var diagnosis = tapMatchDiagnosis(answers, qualityResults);
    var chlorine = resultFor(qualityResults, 'chlorine');
    var eColi = resultFor(qualityResults, 'e_coli');
    var turbidity = resultFor(qualityResults, 'turbidity');
    var hardness = resultFor(qualityResults, 'hardness');
    var copper = resultFor(qualityResults, 'copper');
    var leadResult = resultFor(qualityResults, 'lead');
    var observations = selectedValues(answers, 'observations');
    var priorities = selectedValues(answers, 'priorities');
    var observationLabels = selectedOptionLabelsById('observations', observations);
    var priorityLabels = selectedOptionLabelsById('priorities', priorities);
    var matchedPriorities = priorities;
    var recommendation = matchedRecommendationForAnswers(answers, qualityResults, incidentCount);

    var hero = document.createElement('div');
    hero.className = 'ps-wc-summary-match';
    var copy = document.createElement('div');
    copy.className = 'ps-wc-summary-match-copy';
    var eyebrow = document.createElement('span');
    eyebrow.className = 'ps-wc-summary-match-eyebrow';
    eyebrow.textContent = diagnosis.label;
    var title = document.createElement('h3');
    title.textContent = diagnosis.title;
    var body = document.createElement('p');
    body.textContent = diagnosis.body;
    var selected = document.createElement('div');
    selected.className = 'ps-wc-summary-selected';
    (observationLabels.length ? observationLabels : ['Suburb profile only']).concat(priorityLabels.slice(0, 3)).slice(0, 5).forEach(function (labelText) {
      var pill = document.createElement('span');
      pill.textContent = labelText;
      selected.appendChild(pill);
    });
    var heroActions = document.createElement('div');
    heroActions.className = 'ps-wc-summary-actions';
    var enquire = document.createElement('a');
    enquire.href = '/pages/contact';
    enquire.className = 'ps-wc-btn ps-wc-btn--primary';
    enquire.textContent = 'Ask Purest about my tap';
    var shop = document.createElement('a');
    shop.href = '/collections/all';
    shop.className = 'ps-wc-btn ps-wc-btn--secondary';
    shop.textContent = 'Shop matched systems';
    heroActions.appendChild(enquire);
    heroActions.appendChild(shop);
    copy.appendChild(eyebrow);
    copy.appendChild(title);
    copy.appendChild(body);
    copy.appendChild(selected);
    copy.appendChild(heroActions);

    var metrics = document.createElement('div');
    metrics.className = 'ps-wc-summary-metrics';
    appendSummaryMetric(metrics, 'Chlorine', shortValue(chlorine), 'Taste and smell context');
    appendSummaryMetric(metrics, 'Clarity', shortValue(turbidity), 'Turbidity and visible-particle context');
    appendSummaryMetric(metrics, 'Hardness', shortValue(hardness), 'Scale around taps and kettles');
    appendSummaryMetric(metrics, 'Metals', copper || leadResult ? ((copper ? 'Cu ' + shortValue(copper) : '') + (leadResult ? (copper ? ' / ' : '') + 'Pb ' + shortValue(leadResult) : '')) : null, 'Network context; tapware can still matter');

    hero.appendChild(copy);
    hero.appendChild(metrics);
    els.summaryBlocks.appendChild(hero);

    var proofGrid = document.createElement('div');
    proofGrid.className = 'ps-wc-summary-proof-grid';
    proofGrid.appendChild(summaryProofCard(
      provider ? provider.name : 'Provider not confirmed',
      provider
        ? activeSuburb.suburb + ' uses ' + (zone ? zone.name : provider.name + ' supply context') + ' with ' + qualityCount + ' sourced reading' + (qualityCount === 1 ? '' : 's') + '.'
        : 'The provider is not confirmed in the current dataset.',
      'Local baseline'
    ));
    proofGrid.appendChild(summaryProofCard(
      observationLabels.length ? observationLabels.join(', ') : 'No tap symptom selected',
      observationLabels.length ? 'These selections are matched against the readings above, but they are not treated as a lab result.' : 'The recommendation uses the suburb profile only.',
      'Your tap signals'
    ));
    proofGrid.appendChild(summaryProofCard(
      answers && answers.home_age ? optionLabel(questionById('home_age'), answers.home_age) + (answers.pipes_replaced ? ' / pipes: ' + optionLabel(questionById('pipes_replaced'), answers.pipes_replaced) : '') : 'Home context skipped',
      'Authority data does not inspect your private pipes, fittings, tapware or existing filter cartridges.',
      'Home context'
    ));
    proofGrid.appendChild(summaryProofCard(
      incidentCount > 0 ? incidentCount + ' historical record' + (incidentCount === 1 ? '' : 's') + ' matched' : 'No matched historical incident',
      'If an issue is sudden, severe, happening today, or linked to illness or an active notice, contact the water provider first.',
      'Provider-first check'
    ));
    els.summaryBlocks.appendChild(proofGrid);

    var productBlock = document.createElement('div');
    productBlock.className = 'ps-wc-summary-products';
    var productEyebrow = document.createElement('span');
    productEyebrow.textContent = 'Best Purest match';
    var productTitle = document.createElement('h3');
    productTitle.textContent = 'What this points to';
    var productBody = document.createElement('p');
    productBody.textContent = diagnosis.productNote + ' We can match the exact product to the concern, cartridge specification, flow rate and home setup.';
    productBlock.appendChild(productEyebrow);
    productBlock.appendChild(productTitle);
    productBlock.appendChild(productBody);
    renderProductRecommendations(productBlock, qualityResults, recommendation);
    els.summaryBlocks.appendChild(productBlock);

    updateCtaChips(matchedPriorities);

    setHidden(els.summarySection, false);
    els.summarySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function updateCtaChips(priorities) {
    if (!els.ctaChips) return;
    var chips = els.ctaChips.querySelectorAll('[data-priority]');
    chips.forEach(function (chip) {
      chip.classList.toggle('is-matched', priorities.indexOf(chip.dataset.priority) !== -1);
    });
  }

  /* ── CTA click tracking ────────────────────────────────── */
  if (els.ctaSection) {
    els.ctaSection.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!link) return;
      track('filtration_cta_clicked', { href: link.getAttribute('href') });
    });
  }

  root.addEventListener('click', function (e) {
    var suburbLink = e.target.closest('.ps-wc-suburb-anchor');
    if (suburbLink) {
      var suburbName = suburbLink.getAttribute('data-suburb') || suburbLink.textContent;
      track('water_checker_popular_suburb_clicked', {
        suburb: suburbName
      });
      if (suburbName && els.input) {
        els.input.value = suburbName;
      }
      if (DATA && suburbName) {
        e.preventDefault();
        resolveQuery(suburbName);
      }
      return;
    }

    var sourceLink = e.target.closest('.ps-wc-source-band a');
    if (!sourceLink) return;
    track('water_checker_source_clicked', {
      href: sourceLink.getAttribute('href'),
      label: sourceLink.textContent
    });
  });

  /* ── Boot ─────────────────────────────────────────────── */
  track('water_checker_view', {});
  loadData();

  window.addEventListener('popstate', function () {
    if (!DATA) return;
    handleInitialUrlState();
  });
})();
